import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface FullscreenOverlayProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

// A true full-viewport overlay (unlike Sheet's capped sm:max-w-lg box) -
// for content that wants the whole screen, e.g. a chart in "full
// detalization" mode. Portalled to document.body for the same reason as
// Sheet: a `fixed inset-0` inside an ancestor with its own containing
// block (backdrop-blur, transform, ...) gets squashed into that
// ancestor's box instead of covering the real viewport.
export function FullscreenOverlay({ open, onClose, title, children }: FullscreenOverlayProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-[#1c1c1e]">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-black/10 dark:border-white/10 shrink-0">
        <span className="font-semibold text-[15px] truncate">{title}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close full screen"
          className="shrink-0 p-1.5 rounded-lg text-black/50 dark:text-white/50 hover:text-current hover:bg-black/5 dark:hover:bg-white/10"
        >
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
    </div>,
    document.body
  );
}
